import { useState, useEffect, useMemo } from 'react';

import { dataGridPremiumColumns, getContrastColor } from '../../utils/helper';
import { DatagridPremiumUi, GridCellExpand } from '../Interface';
import { DocStatusList } from '../../config/document';
import documentService from '../../service/newDoc.service';
import dayjs from 'dayjs';
import { Box, Checkbox, Chip, CircularProgress, Tooltip } from '@mui/material';
import { DocumentDirection } from '../../config/DocumentDirection';
import { getProjectOrderNumberName } from '../../utils/getProjectOrderNumberName';
import useParam from '../../context/ParamContext';
import { formatCurrency, getRounding } from '../../utils/currencyHelper';
import useGenerateJokerReportCols from '../../hooks/useGenerateJokerReportCols';
import useConfig from '../../context/ConfigContext';
import useUsers from '../../context/UsersContext';
import addNotification from '../../utils/addNotification';
import { NotificationType } from '../../config';

const DocumentReports = ({ docState }) => {
  const {
    setRefetchTable,
    refetchTable,
    viewField,
    setSelectedValues,
    operatorsDisabled,
    setRowSelectionModel,
    autocompleteOptions,
    autocompleteData,
    datagridIdentifiers,
    rowSelectionModel,
    docTypeId,
    documentData,
    jokerFields,
  } = docState;

  const [reports, setReports] = useState({ rows: [], rowCount: 0 });
  const [pendingSettlementDocIds, setPendingSettlementDocIds] = useState([]);
  const [pendingInvoiceConfirmationDocIds, setPendingInvoiceConfirmationDocIds] = useState([]);
  const { params } = useParam();
  const { config } = useConfig();
  const { user } = useUsers();

  const resolveCustomStatus = (row) => {
    if (row?.customDocStatus) return row.customDocStatus;
    if (row?.customStatusData) return row.customStatusData;
    return autocompleteData?.customStatus?.find((status) => status.code === row?.customStatus) ?? null;
  };

  const resolveDocumentPiority = (row) => {
    if (row?.documentPiority) return row.documentPiority;
    return autocompleteData?.documentPiority?.find((priority) => priority.priorityId === row?.priorityId) ?? null;
  };

  const initialState = {
    filter: { filterModel: { items: [], logicOperator: 'and' } },
    sorting: {
      sortModel: [{ field: 'docNum', sort: 'asc' }],
    },
    pagination: {
      paginationModel: {
        pageSize: 25,
        page: 0,
      },
    },
    density: 'compact',
  };

  const getRowRounding = (row) => {
    const currency = autocompleteData.currency.find((c) => c.code === row.currencyCode);

    return getRounding(documentData.direction, currency);
  };

  const getOwnRowRounding = () => {
    const currency = autocompleteData.currency.find((c) => c.code === config.currencyCode);
    return getRounding(documentData.direction, currency);
  };

  useEffect(() => {
    if (operatorsDisabled) {
      setRowSelectionModel([]);
    }
  }, [operatorsDisabled]);

  const getList = (gridState) => {
    documentService.getDocuments(gridState, docTypeId).then((data) => {
      setReports(data);
    });
  };

  const hidedFieldNames = JSON.parse(params.DOCUMENT_HIDE_FIELDS ?? '{}');
  const skipField = (fieldName) => (hidedFieldNames[docTypeId] || []).includes(fieldName);

  const jokerCols = useGenerateJokerReportCols(jokerFields);

  const renderCellExpand = (data) => {
    return <GridCellExpand value={data.value || ''} width={data.colDef.computedWidth} />;
  };

  const isSettlementPending = (docId) => pendingSettlementDocIds.includes(docId);

  const handleAutoSettlementChange = async (event, row) => {
    event.stopPropagation();

    if (!row || row.balanceSettled || isSettlementPending(row.docId)) {
      return;
    }

    setPendingSettlementDocIds((prev) => [...prev, row.docId]);

    try {
      const updatedRow = await documentService.autoCreateSettlement({
        docId: row.docId,
        docTypeId: row.docTypeId ?? docTypeId,
      });

      setReports((prev) => ({
        ...prev,
        rows: prev.rows.map((reportRow) =>
          reportRow.docId === row.docId ? { ...reportRow, ...updatedRow } : reportRow
        ),
      }));

      if (rowSelectionModel?.includes?.(row.docId)) {
        setSelectedValues(updatedRow);
      }

      setRefetchTable(true);
      addNotification({
        content: 'A kiegyenlítés sikeresen létrejött.',
        type: NotificationType.SUCCESS,
      });
    } catch (_error) {
      addNotification({
        content: 'A kiegyenlítés automatikus létrehozása nem sikerült.',
        type: NotificationType.ERROR,
      });
    } finally {
      setPendingSettlementDocIds((prev) => prev.filter((docId) => docId !== row.docId));
    }
  };

  const renderSettlementStatusCell = (data) => {
    const pending = isSettlementPending(data.row.docId);
    const isSettled = Boolean(data.row.balanceSettled);

    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          overflow: 'visible',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            minWidth: 'fit-content',
            flexShrink: 1,
          }}
        >
          {isSettled ? (
            <Chip label="Kiegyenlített" color="success" size="small" variant="outlined" />
          ) : (
            <Chip
              label={`${documentData.direction === DocumentDirection.INCOMING ? 'Követel' : 'Tartozik'}`}
              color="error"
              size="small"
              variant="outlined"
            />
          )}
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            width: 'auto',
            flexShrink: 0,
          }}
        >
          {pending ? (
            <CircularProgress size={18} />
          ) : (
            <Tooltip
              title={
                isSettled
                  ? 'A már kiegyenlített bizonylat itt nem módosítható.'
                  : 'Automatikus kiegyenlítés létrehozása'
              }
            >
              <span>
                <Checkbox
                  size="small"
                  checked={isSettled}
                  disabled={isSettled}
                  sx={{ p: 0 }}
                  onMouseDown={(event) => {
                    event.stopPropagation();
                  }}
                  onClick={(event) => {
                    event.stopPropagation();
                  }}
                  onChange={(event) => handleAutoSettlementChange(event, data.row)}
                />
              </span>
            </Tooltip>
          )}
        </Box>
      </Box>
    );
  };

  const handleInvoiceConfirmationChange = async (event, row) => {
    event.stopPropagation();

    if (!row || pendingInvoiceConfirmationDocIds.includes(row.docId)) {
      return;
    }

    const invoiceConfirmation = event.target.checked;
    setPendingInvoiceConfirmationDocIds((prev) => [...prev, row.docId]);

    try {
      const updatedRow = await documentService.updateDocument(
        {
          ...row,
          invoiceConfirmation,
          updatedBy: user.userId,
        },
        row.docId
      );
      const nextRow = { ...row, ...updatedRow, invoiceConfirmation };

      setReports((prev) => ({
        ...prev,
        rows: prev.rows.map((reportRow) => (reportRow.docId === row.docId ? nextRow : reportRow)),
      }));

      if (rowSelectionModel?.includes?.(row.docId)) {
        setSelectedValues((prev) => ({ ...prev, ...nextRow }));
      }

      setRefetchTable(true);
      addNotification({
        content: 'A számlaigazolás sikeresen módosult.',
        type: NotificationType.SUCCESS,
      });
    } catch (_error) {
      addNotification({
        content: 'A számlaigazolás módosítása nem sikerült.',
        type: NotificationType.ERROR,
      });
    } finally {
      setPendingInvoiceConfirmationDocIds((prev) => prev.filter((docId) => docId !== row.docId));
    }
  };

  const renderInvoiceConfirmationCell = (data) => {
    const pending = pendingInvoiceConfirmationDocIds.includes(data.row.docId);

    if (pending) {
      return <CircularProgress size={18} />;
    }

    return (
      <Checkbox
        size="small"
        checked={Boolean(data.row.invoiceConfirmation)}
        sx={{ p: 0 }}
        onMouseDown={(event) => {
          event.stopPropagation();
        }}
        onClick={(event) => {
          event.stopPropagation();
        }}
        onChange={(event) => handleInvoiceConfirmationChange(event, data.row)}
      />
    );
  };

  const showCompanyColumns = Boolean(documentData?.company || documentData?.cooperation);

  const customStatusRowSx = useMemo(() => {
    return (reports.rows ?? []).reduce((styles, row) => {
      const status = resolveCustomStatus(row);
      if (!status?.color) return styles;

      const textColor = getContrastColor(status.color);
      const rowSelector = `& .custom-status-row-${row.docId} .MuiDataGrid-cell`;
      const hoverSelector = `& .custom-status-row-${row.docId}:hover .MuiDataGrid-cell`;

      styles[rowSelector] = {
        backgroundColor: `${status.color} !important`,
        color: `${textColor} !important`,
      };
      styles[hoverSelector] = {
        backgroundColor: `${status.color} !important`,
        color: `${textColor} !important`,
      };

      return styles;
    }, {});
  }, [reports.rows, autocompleteData.customStatus]);

  const columns = useMemo(() => {
    return dataGridPremiumColumns([
      ...(!skipField('docNum')
        ? [
            {
              field: 'docNum',
              headerName: 'Bizonylatszám',
              type: 'string',
            },
          ]
        : []),
      ...(!skipField('docStatus')
        ? [
            {
              field: 'docStatus',
              headerName: 'Státusz',
              type: 'singleSelect',
              sortable: false,
              valueOptions: DocStatusList,
            },
          ]
        : []),
      ...(!skipField('customStatus')
        ? [
            {
              field: 'customStatus',
              headerName: 'Egyedi státusz',
              type: 'singleSelect',
              renderCell: (data) => resolveCustomStatus(data.row)?.name || '',
              valueOptions: autocompleteOptions.customStatus,
            },
          ]
        : []),
      ...(documentData?.useDocumentPriority && !skipField('priorityId')
        ? [
            {
              field: 'priorityId',
              headerName: 'Prioritás',
              type: 'singleSelect',
              valueOptions: autocompleteOptions.documentPiority,
              renderCell: (data) => {
                const priority = resolveDocumentPiority(data.row);
                const color = priority?.color;

                return (
                  <Box
                    sx={{
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      px: 1,
                      ...(color
                        ? { backgroundColor: `${color} !important`, color: `${getContrastColor(color)} !important` }
                        : {}),
                    }}
                  >
                    {priority?.name ?? ''}
                  </Box>
                );
              },
            },
          ]
        : []),
      {
        field: 'createdBy',
        headerName: 'Létrehozta',
        type: 'singleSelect',
        renderCell: (data) => {
          return (
            data.row.user?.userName ||
            autocompleteData?.users?.find((user) => user.userId === data.row.createdBy)?.userName ||
            ''
          );
        },
        valueOptions: autocompleteOptions.users,
      },
      ...(showCompanyColumns
        ? [
            ...(!skipField('companyId')
              ? [
                  {
                    field: 'companyId',
                    headerName: 'Cég',
                    type: 'singleSelect',
                    sortable: false,
                    renderCell: (data) => {
                      return data.row.company?.code;
                    },
                    valueOptions: autocompleteOptions.company,
                  },
                ]
              : []),
            ...(!skipField('companyName')
              ? [
                  {
                    field: 'companyName',
                    headerName: 'Cég neve',
                    type: 'string',
                  },
                ]
              : []),
            {
              field: 'companyContactId',
              headerName: 'Kapcsolattartó',
              type: 'string',
              sortable: false,
              renderCell: (data) => {
                return data.row.companyContact?.name ?? '';
              },
            },
          ]
        : []),
      ...(!skipField('docDate')
        ? [
            {
              field: 'docDate',
              headerName: 'Teljesítés dátuma',
              type: 'date',
              valueGetter: (_data, row) => {
                return new Date(row.docDate);
              },
              renderCell: (data) => {
                return dayjs(data.row.docDate).format('YYYY-MM-DD');
              },
            },
          ]
        : []),
      ...(!skipField('issueDate')
        ? [
            {
              field: 'issueDate',
              headerName: 'Kiállítás dátuma',
              type: 'date',
              valueGetter: (_data, row) => {
                return new Date(row.issueDate);
              },
              renderCell: (data) => {
                return dayjs(data.row.issueDate).format('YYYY-MM-DD');
              },
            },
          ]
        : []),
      ...(!skipField('dueDate')
        ? [
            {
              field: 'dueDate',
              headerName: 'Határidő',
              type: 'date',
              valueGetter: (_data, row) => {
                return new Date(row.dueDate);
              },
              renderCell: (data) => {
                return dayjs(data.row.dueDate).format('YYYY-MM-DD');
              },
            },
          ]
        : []),
      ...(!skipField('paymentMethodId')
        ? [
            {
              field: 'paymentMethodId',
              headerName: 'Fizetési mód',
              type: 'singleSelect',
              valueOptions: autocompleteOptions.paymentMethod,
            },
          ]
        : []),
      ...(!skipField('attachment')
        ? [
            {
              field: 'attachment',
              headerName: 'Csatolmány',
              type: 'boolean',
              sortable: false,
              filterable: false,
              valueGetter: (_data, row) => (row.attachment?.length ?? 0) > 0,
            },
          ]
        : []),
      ...(documentData?.employee && !skipField('employeeId')
        ? [
            {
              field: 'employeeId',
              headerName: 'Dolgozó',
              type: 'singleSelect',
              sortable: false,
              renderCell: (data) => {
                return data.row.employee?.name;
              },
              valueOptions: autocompleteOptions.employee,
            },
          ]
        : []),
      ...(documentData?.project
        ? [
            ...(!skipField('projectId')
              ? [
                  {
                    field: 'projectId',
                    headerName: 'Projekt',
                    type: 'string',
                    sortable: false,
                    renderCell: (data) => {
                      return data.row.project?.projectName;
                    },
                  },
                ]
              : []),
            ...(!skipField('projectOrderNumber')
              ? [
                  {
                    field: 'projectOrderNumber',
                    headerName: getProjectOrderNumberName(params),
                    type: 'string',
                    sortable: false,
                    renderCell: (data) => {
                      return data.row.project?.orderNumber;
                    },
                  },
                ]
              : []),
          ]
        : []),
      ...(!skipField('reference')
        ? [
            {
              field: 'reference',
              headerName: 'Hivatkozási szám',
              type: 'string',
            },
          ]
        : []),
      {
        field: 'salesOrPurchasePersonId',
        headerName: documentData.direction === DocumentDirection.INCOMING ? 'Beszerző' : 'Értékesítő',
        type: 'singleSelect',
        sortable: false,
        renderCell: (data) => {
          return data.row.salesOrPurchasePerson?.name ?? '';
        },
        valueOptions: autocompleteOptions.salesOrPurchasePerson,
      },
      ...(!skipField('costCenterCode')
        ? [
            {
              field: 'costCenterCode',
              headerName: 'Költséghely',
              type: 'singleSelect',
              sortable: false,
              renderCell: (data) => {
                return data.row.costCenter?.name;
              },
              valueOptions: autocompleteOptions.costCenter,
            },
          ]
        : []),
      ...(documentData?.invoiceConfirmation
        ? [
            {
              field: 'invoiceConfirmation',
              headerName: 'Számlaigazolás',
              type: 'boolean',
              sortable: false,
              filterable: false,
              align: 'center',
              headerAlign: 'center',
              renderCell: renderInvoiceConfirmationCell,
            },
          ]
        : []),
      ...(documentData?.enableSettlement
        ? [
            {
              field: 'balanceSettled',
              headerName: 'Kintlévőség',
              type: 'singleSelect',
              sortable: false,
              align: 'right',
              headerAlign: 'right',
              renderCell: renderSettlementStatusCell,
              valueOptions: [
                { value: true, label: 'Kiegyenlített' },
                {
                  value: false,
                  label: documentData.direction === DocumentDirection.INCOMING ? 'Követel' : 'Tartozik',
                },
              ],
            },
            {
              field: 'settledAmount',
              headerName: 'Eddig fizetve',
              type: 'number',
              renderCell: (data) => {
                return formatCurrency(data.row.settledAmount);
              },
            },
            {
              field: 'openAmount',
              headerName: 'Nyitott összeg',
              type: 'number',
              renderCell: (data) => {
                return formatCurrency(data.row.openAmount, getRowRounding(data.row));
              },
            },
          ]
        : []),
      ...(!skipField('currencyCode')
        ? [
            {
              field: 'currencyCode',
              headerName: 'Pénznem',
              type: 'singleSelect',
              sortable: false,
              renderCell: (data) => {
                return data.row.currencyCode;
              },
              valueOptions: autocompleteOptions.currency,
            },
          ]
        : []),
      {
        field: 'netInDocCur',
        headerName: 'Összesen nettó',
        type: 'number',
        sortable: false,
        renderCell: (data) => {
          return formatCurrency(data.row.netInDocCur, getRowRounding(data.row));
        },
      },
      {
        field: 'VATInDocCur',
        headerName: 'Összesen Áfa',
        type: 'number',
        sortable: false,
        renderCell: (data) => {
          return formatCurrency(data.row.VATInDocCur, getRowRounding(data.row));
        },
      },
      {
        field: 'sumInDocCur',
        headerName: 'Összesen bruttó',
        type: 'number',
        sortable: false,
        renderCell: (data) => {
          return formatCurrency(data.row.sumInDocCur, getRowRounding(data.row));
        },
      },
      {
        field: 'netInOwnCur',
        headerName: 'Összesen nettó saját pénznemben',
        type: 'number',
        sortable: false,
        renderCell: (data) => {
          return formatCurrency(data.row.netInOwnCur, getOwnRowRounding());
        },
      },
      {
        field: 'VATInOwnCur',
        headerName: 'Összesen Áfa saját pénznemben',
        type: 'number',
        sortable: false,
        renderCell: (data) => {
          return formatCurrency(data.row.VATInOwnCur, getOwnRowRounding());
        },
      },
      {
        field: 'sumInOwnCur',
        headerName: 'Összesen bruttó saját pénznemben',
        type: 'number',
        sortable: false,
        renderCell: (data) => {
          return formatCurrency(data.row.sumInOwnCur, getOwnRowRounding());
        },
      },
      {
        field: 'note',
        headerName: 'Megjegyzés',
        type: 'string',
        renderCell: renderCellExpand,
      },
      ...jokerCols,
    ]);
  }, [
    autocompleteOptions,
    autocompleteData.customStatus,
    autocompleteData.documentPiority,
    documentData,
    jokerCols,
    showCompanyColumns,
    pendingSettlementDocIds,
    pendingInvoiceConfirmationDocIds,
    user.userId,
  ]);

  return (
    <>
      {Object.keys(autocompleteOptions).length > 0 && Object.keys(documentData).length > 0 && (
        <DatagridPremiumUi
          key={datagridIdentifiers.doc}
          datagridIdentifier={datagridIdentifiers.doc}
          datagridInitialState={initialState}
          dataLoader={getList}
          rows={reports.rows}
          refetch={{ refetchTable, setRefetchTable }}
          rowCount={reports.rowCount}
          columns={[...columns]}
          onRowClick={(rowData) => {
            if (!viewField) return;
            setSelectedValues(rowData.row);
          }}
          paginationAndfilterMode="server"
          onRowSelectionModelChange={(newRowSelectionModel) => {
            setRowSelectionModel(newRowSelectionModel);
          }}
          rowSelectionModel={rowSelectionModel}
          disableRowSelectionOnClick={!viewField}
          getRowId={(row) => row.docId}
          getRowClassName={(params) => (params.row?.customStatus ? `custom-status-row-${params.row.docId}` : '')}
          sx={customStatusRowSx}
        />
      )}
    </>
  );
};

export default DocumentReports;
